import { ALERT, alertCallbackStore, API_KEYS } from '@flexo/reducers';
import { IconWrapper } from '@flexo/general';
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../button/button';
import styles from './table.module.scss';
import uStyles from './admin-users-table.module.scss';

interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

export function ApiKeysTable() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const apiKeysState = useSelector((state: any) => state.apiKeys);
  const { apiKeys, loading, error } = apiKeysState;

  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  useEffect(() => {
    dispatch(API_KEYS.FetchApiKeysAsync() as any);
  }, [dispatch]);

  const handleSort = (field: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig?.field === field && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ field, direction });
  };

  const sortedKeys = [...(apiKeys || [])].sort((a, b) => {
    if (!sortConfig) return 0;
    const aVal = a[sortConfig.field] || '';
    const bVal = b[sortConfig.field] || '';
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleCreateKey = () => {
    dispatch(
      ALERT.SetAlert({
        value: {
          type: 'ALERT_CREATE_API_KEY',
        },
      } as any)
    );
  };

  const handleRevokeKey = (keyName: string) => {
    alertCallbackStore.setData({ keyName });
    dispatch(
      ALERT.SetAlert({
        value: {
          type: 'ALERT_REVOKE_API_KEY',
        },
      } as any)
    );
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const columns = [
    { field: 'name', title: t('widgets.apiKeys.columns.name'), sortable: true },
    { field: 'status', title: t('widgets.apiKeys.columns.status'), sortable: true },
    { field: 'createdAt', title: t('widgets.apiKeys.columns.createdAt'), sortable: true },
    { field: 'serviceAccountEmail', title: t('widgets.apiKeys.columns.serviceAccount'), sortable: false },
    { field: 'actions', title: t('widgets.apiKeys.columns.actions'), sortable: false },
  ];

  return (
    <div className={styles.TableWrapper}>
      <div className={`${styles.TableHeader} flex flex-row flex-justify-between flex-align-center`}>
        <h2>{t('widgets.apiKeys.title')}</h2>
        <Button
          style={{ maxHeight: 'fit-content', maxWidth: 'fit-content', padding: '8px 24px' }}
          color="secondary"
          label={t('widgets.apiKeys.createButton')}
          onClick={handleCreateKey}
        />
      </div>

      {error && (
        <div style={{ color: 'var(--hive-negative-500)', padding: '8px 0' }}>
          {error}
        </div>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.field}>
                  {col.sortable ? (
                    <TableSortLabel
                      active={sortConfig?.field === col.field}
                      direction={sortConfig?.field === col.field ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort(col.field)}
                    >
                      {col.title}
                    </TableSortLabel>
                  ) : (
                    col.title
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  {t('common.loading', { defaultValue: 'Loading...' })}
                </TableCell>
              </TableRow>
            ) : sortedKeys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  {t('widgets.apiKeys.noKeys')}
                </TableCell>
              </TableRow>
            ) : (
              sortedKeys.map((key: any) => {
                const status = key.isActive ? 'active' : 'revoked';
                return (
                <TableRow key={key.name}>
                  <TableCell>{key.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={t(`widgets.apiKeys.status.${status}`)}
                      color={status === 'active' ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(key.createdAt)}</TableCell>
                  <TableCell>{key.serviceAccountEmail || '-'}</TableCell>
                  <TableCell>
                    {status === 'active' && (
                      <div
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleRevokeKey(key.name)}
                      >
                        <IconWrapper
                          className={uStyles.AdminUsers__Icon}
                          iconName="delete_circle"
                        />
                      </div>
                    )}
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export default ApiKeysTable;
